import React from 'react';

import { Alert, Col, Flex, Row } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { routeParamsSel } from '@modules/location/duck/selectorsTyped';
import { UnitRosterContainer } from '@modules/roster-lite/components/UnitRosterPage/UnitRosterPage';
import { canViewYouthAdvancementDetailsSel } from '@modules/roster-lite/duck/selectors';
import { useYouthProfile } from '@modules/roster-lite/duck/youthDetailsHooks';
import PageContainer from '@shared/components/Layouts/PageContainer';
import ReturnLink from '@shared/components/ReturnLink';
import YouthProfileCard from '@shared/components/YouthProfileCard';

import YouthAwards from './YouthAwards';
import YouthLastRank from './YouthLastRank';
import YouthMeritBadges from './YouthMeritBadges';
import YouthRanks from './YouthRanks';

interface YouthProfileRouteParams {
  orgGuid?: string;
  /** Scoutbook user id (`sb_user_id` from the roster report), not the BSA member id. */
  userId?: string;
}

const YouthAdvancementProfileContents: React.FC = () => {
  const { orgGuid, userId } = useSelector(
    routeParamsSel<YouthProfileRouteParams>,
  );
  const allowed = useSelector(canViewYouthAdvancementDetailsSel);
  const {
    profile,
    akelaProfile,
    activeProgram,
    loading,
    registration,
    registrationLoading,
    isError,
  } = useYouthProfile();

  // Same args as the hook's query, so YouthLastRank reads the same cache entry
  const numericUserId = Number(userId) || undefined;

  // The route thunk already 403s denied users; this guard keeps the page from
  // rendering (and fetching) if the component is ever mounted another way
  if (!allowed) return null;

  return (
    <PageContainer>
      <Flex vertical gap="middle">
        {orgGuid && (
          <ReturnLink to={`/unit-roster/${orgGuid}`}>
            <FormattedMessage id="unitRoster.youthProfile.backToRoster" />
          </ReturnLink>
        )}
        {isError && !loading ? (
          <Alert
            type="error"
            showIcon
            message={
              <FormattedMessage id="unitRoster.youthProfile.error.title" />
            }
            description={
              <FormattedMessage id="unitRoster.youthProfile.error" />
            }
          />
        ) : (
          // Side by side from `lg` up, stacked below it
          <Row gutter={[16, 16]} align="top">
            <Col xs={24} lg={8} xl={7}>
              <YouthProfileCard
                profile={profile}
                akelaProfile={akelaProfile}
                activeProgram={activeProgram}
                loading={loading}
                registration={registration}
                registrationLoading={registrationLoading}
                lastRank={<YouthLastRank userId={numericUserId} />}
              />
            </Col>
            <Col xs={24} lg={16} xl={17}>
              {/* Same order as the legacy youth profile: ranks, then badges, then awards */}
              <Flex vertical gap="middle">
                <YouthRanks userId={userId} activeProgram={activeProgram} />
                <YouthMeritBadges userId={userId} />
                <YouthAwards userId={userId} />
              </Flex>
            </Col>
          </Row>
        )}
      </Flex>
    </PageContainer>
  );
};

export default function YouthAdvancementProfilePage() {
  return (
    <UnitRosterContainer>
      <YouthAdvancementProfileContents />
    </UnitRosterContainer>
  );
}
